import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faUserFriends,
  faCheck,
  faBan,
  faComments,
  faUserCheck,
  faUserLock,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { matchesWordStart } from '../../utils/searchMatch';
import chatAccessService from '../../services/chatAccessService';
import practiceService from '../../services/practiceService';
import { canManageChatAccess, CHAT_PAIR_TYPES } from '../../utils/chatPermissions';
import { ROLES } from '../../utils/roleUtils';
import '../PracticeManagement/GlobalDashboard/GlobalDashboard.css';
import './ChatAccess.css';

const PAIR_TABS = [
  {
    value: CHAT_PAIR_TYPES.CAREGIVER_PATIENT,
    label: 'Caregiver ↔ Patient',
    primaryLabel: 'Caregiver',
    partnerLabel: 'Patients',
  },
  {
    value: CHAT_PAIR_TYPES.PROVIDER_PATIENT,
    label: 'Provider ↔ Patient',
    primaryLabel: 'Provider',
    partnerLabel: 'Patients',
  },
  {
    value: CHAT_PAIR_TYPES.CAREGIVER_PROVIDER,
    label: 'Caregiver ↔ Provider',
    primaryLabel: 'Caregiver',
    partnerLabel: 'Providers',
  },
];

const PARTNER_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'allowed', label: 'Allowed' },
  { value: 'blocked', label: 'Blocked' },
];

function getInitials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function userSearchText(user) {
  return `${user?.name || ''} ${user?.username || ''} ${user?.role_name || ''}`.toLowerCase();
}

const ChatAccess = ({ embedded = false } = {}) => {
  const { user } = useSelector((state) => state.auth);
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const roleId = Number(user?.role_id);
  const canPickPractice =
    roleId === ROLES.SUPER_ADMIN || roleId === ROLES.SYSTEM_ADMIN;

  const [practices, setPractices] = useState([]);
  const [practiceId, setPracticeId] = useState(user?.practice_id ? String(user.practice_id) : '');
  const [pairType, setPairType] = useState(CHAT_PAIR_TYPES.CAREGIVER_PATIENT);
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [selectedPrimaryId, setSelectedPrimaryId] = useState(null);
  const [primarySearch, setPrimarySearch] = useState('');
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('all');

  const activeTab = PAIR_TABS.find((t) => t.value === pairType) || PAIR_TABS[0];

  useEffect(() => {
    if (embedded) {
      updateBreadcrumbs([
        { label: 'Settings', path: '/settings' },
        { label: 'Chat Access', path: '/settings?tab=chat-access', active: true },
      ]);
    } else {
      updateBreadcrumbs([
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Chat Access', path: '/chat-access', active: true },
      ]);
    }
    return () => clearBreadcrumbs();
  }, [updateBreadcrumbs, clearBreadcrumbs, embedded]);

  useEffect(() => {
    if (!canManageChatAccess(roleId)) return undefined;
    if (!canPickPractice) {
      if (user?.practice_id) setPracticeId(String(user.practice_id));
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingPractices(true);
        const res = await practiceService.getPractices({ page: 1, limit: 1000 });
        const payload = res?.data;
        let list = [];
        if (payload?.success && payload?.data?.practices) {
          list = payload.data.practices;
        } else if (payload?.success && Array.isArray(payload?.data)) {
          list = payload.data;
        } else if (Array.isArray(payload?.data)) {
          list = payload.data;
        } else if (Array.isArray(payload)) {
          list = payload;
        }
        if (!cancelled) {
          setPractices(list);
          if (!practiceId && list[0]?.id) setPracticeId(String(list[0].id));
        }
      } catch {
        if (!cancelled) toast.error('Failed to load practices');
      } finally {
        if (!cancelled) setLoadingPractices(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canPickPractice, roleId, user?.practice_id]);

  const loadPairs = useCallback(async () => {
    if (!canManageChatAccess(roleId) || !practiceId || !user?.id) {
      setPairs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await chatAccessService.listPairs({
        practiceId,
        pairType,
        managerUserId: user.id,
      });
      const data = res?.data?.data || res?.data;
      setPairs(Array.isArray(data?.pairs) ? data.pairs : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load chat access pairs');
      setPairs([]);
    } finally {
      setLoading(false);
    }
  }, [practiceId, pairType, roleId, user?.id]);

  useEffect(() => {
    loadPairs();
  }, [loadPairs]);

  useEffect(() => {
    setSelectedPrimaryId(null);
    setPrimarySearch('');
    setPartnerSearch('');
    setPartnerFilter('all');
  }, [practiceId, pairType]);

  const primaryUsers = useMemo(() => {
    const map = new Map();
    pairs.forEach((pair) => {
      const primary = pair.user_a;
      if (!primary?.id) return;
      const id = Number(primary.id);
      if (!map.has(id)) {
        map.set(id, {
          ...primary,
          id,
          allowedCount: 0,
          totalCount: 0,
        });
      }
      const entry = map.get(id);
      entry.totalCount += 1;
      if (pair.allowed) entry.allowedCount += 1;
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''))
    );
  }, [pairs]);

  const filteredPrimaries = useMemo(() => {
    if (!primarySearch.trim()) return primaryUsers;
    return primaryUsers.filter((u) => matchesWordStart(userSearchText(u), primarySearch));
  }, [primaryUsers, primarySearch]);

  useEffect(() => {
    if (!filteredPrimaries.length) {
      setSelectedPrimaryId(null);
      return;
    }
    if (!selectedPrimaryId || !filteredPrimaries.some((u) => u.id === selectedPrimaryId)) {
      setSelectedPrimaryId(filteredPrimaries[0].id);
    }
  }, [filteredPrimaries, selectedPrimaryId]);

  const selectedPrimary = useMemo(
    () => primaryUsers.find((u) => u.id === selectedPrimaryId) || null,
    [primaryUsers, selectedPrimaryId]
  );

  const partnerRows = useMemo(() => {
    if (!selectedPrimaryId) return [];
    return pairs
      .filter((p) => Number(p.user_a?.id) === Number(selectedPrimaryId))
      .map((pair) => ({
        ...pair,
        partner: pair.user_b,
      }));
  }, [pairs, selectedPrimaryId]);

  const filteredPartners = useMemo(() => {
    return partnerRows.filter((row) => {
      if (partnerFilter === 'allowed' && !row.allowed) return false;
      if (partnerFilter === 'blocked' && row.allowed) return false;
      if (!partnerSearch.trim()) return true;
      return matchesWordStart(userSearchText(row.partner), partnerSearch);
    });
  }, [partnerRows, partnerSearch, partnerFilter]);

  const upsertPair = async (pair, nextAllowed) => {
    await chatAccessService.upsertGrant({
      practice_id: Number(practiceId),
      pair_type: pairType,
      user_a_id: pair.user_a.id,
      user_b_id: pair.user_b.id,
      allowed: nextAllowed,
      manager_user_id: user.id,
    });
    setPairs((prev) =>
      prev.map((row) =>
        row.user_low_id === pair.user_low_id && row.user_high_id === pair.user_high_id
          ? { ...row, allowed: nextAllowed }
          : row
      )
    );
  };

  const handleToggle = async (pair) => {
    if (!user?.id || !practiceId || bulkSaving) return;
    const key = `${pair.user_low_id}-${pair.user_high_id}`;
    setSavingKey(key);
    const nextAllowed = !pair.allowed;
    try {
      await upsertPair(pair, nextAllowed);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update chat access');
    } finally {
      setSavingKey(null);
    }
  };

  const handleBulk = async (allow) => {
    if (!user?.id || !practiceId || !selectedPrimaryId || !partnerRows.length) return;
    const targets = partnerRows.filter((row) => Boolean(row.allowed) !== allow);
    if (!targets.length) {
      toast.info(allow ? 'Already allowed for all partners' : 'Already blocked for all partners');
      return;
    }

    const previousPairs = pairs;
    setPairs((prev) =>
      prev.map((row) =>
        Number(row.user_a?.id) === Number(selectedPrimaryId)
          ? { ...row, allowed: allow }
          : row
      )
    );
    setBulkSaving(true);

    try {
      await chatAccessService.bulkUpsertGrants({
        practice_id: Number(practiceId),
        pair_type: pairType,
        primary_user_id: selectedPrimaryId,
        partner_user_ids: partnerRows.map((row) => row.user_b.id),
        allowed: allow,
        manager_user_id: user.id,
      });
      toast.success(
        allow
          ? `Allowed chat with all ${activeTab.partnerLabel.toLowerCase()}`
          : `Blocked chat with all ${activeTab.partnerLabel.toLowerCase()}`
      );
    } catch (err) {
      setPairs(previousPairs);
      toast.error(err?.response?.data?.message || 'Failed to update chat access');
    } finally {
      setBulkSaving(false);
    }
  };

  if (!canManageChatAccess(roleId)) {
    return (
      <div className={`dashboard-container ca-page${embedded ? ' ca-page--embedded' : ''}`}>
        {!embedded && (
          <div className="dashboard-header">
            <h2 className="dashboard-title">Chat Access</h2>
          </div>
        )}
        <div className="ca-placeholder-card">
          <FontAwesomeIcon icon={faComments} className="ca-placeholder-icon" />
          <h4>Access restricted</h4>
          <p>Only Super Admin, System Admin, and Practice Admin can manage chat permissions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-container ca-page${embedded ? ' ca-page--embedded' : ''}`}>
      {!embedded && (
        <div className="dashboard-header">
          <h2 className="dashboard-title">Chat Access</h2>
        </div>
      )}

      <div className="ca-filters-wrap">
        <div className="dashboard-filters ca-dashboard-filters">
          {canPickPractice && (
            <select
              className="dashboard-select ca-practice-select"
              value={practiceId}
              onChange={(e) => setPracticeId(e.target.value)}
              disabled={loadingPractices}
              aria-label="Select practice"
            >
              <option value="">
                {loadingPractices ? 'Loading practices…' : 'Practice'}
              </option>
              {practices.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.practice_name || p.name || `Practice ${p.id}`}
                </option>
              ))}
            </select>
          )}

          <div className="ca-pair-tabs" role="tablist" aria-label="Chat pair type">
            {PAIR_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={pairType === tab.value}
                className={`ca-pair-tab${pairType === tab.value ? ' is-active' : ''}`}
                onClick={() => setPairType(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!practiceId ? (
        <div className="ca-placeholder-card">
          <FontAwesomeIcon icon={faBuilding} className="ca-placeholder-icon" />
          <h4>Select a practice</h4>
          <p>Choose a practice to manage caregiver, provider, and patient chat permissions.</p>
        </div>
      ) : loading ? (
        <div className="ca-placeholder-card">
          <p>Loading people…</p>
        </div>
      ) : primaryUsers.length === 0 ? (
        <div className="ca-placeholder-card">
          <FontAwesomeIcon icon={faUserFriends} className="ca-placeholder-icon" />
          <h4>No people found</h4>
          <p>No {activeTab.primaryLabel.toLowerCase()}s or partners found for this practice.</p>
        </div>
      ) : (
        <div className="ca-workspace">
          <aside className="ca-panel ca-panel--primary">
            <div className="ca-panel-head">
              <div className="ca-panel-title-row">
                <h3>{activeTab.primaryLabel}s</h3>
                <span className="ca-count">{filteredPrimaries.length}</span>
              </div>
              <div className="search-box-wrap ca-search-box">
                <FontAwesomeIcon icon={faSearch} className="search-icon" aria-hidden="true" />
                <input
                  type="search"
                  value={primarySearch}
                  onChange={(e) => setPrimarySearch(e.target.value)}
                  placeholder={`Search ${activeTab.primaryLabel.toLowerCase()}s`}
                  aria-label={`Search ${activeTab.primaryLabel}s`}
                />
              </div>
            </div>

            <div className="ca-list">
              {filteredPrimaries.length === 0 ? (
                <div className="ca-empty-inline">No matches.</div>
              ) : (
                filteredPrimaries.map((person, index) => {
                  const isActive = person.id === selectedPrimaryId;
                  return (
                    <button
                      key={person.id}
                      type="button"
                      className={`ca-person${isActive ? ' is-active' : ''}${index % 2 === 1 ? ' ca-person--alt' : ''}`}
                      onClick={() => setSelectedPrimaryId(person.id)}
                    >
                      <span className="ca-avatar">{getInitials(person.name)}</span>
                      <span className="ca-person-meta">
                        <strong>{person.name}</strong>
                        <span>{person.username || person.role_name}</span>
                      </span>
                      <span
                        className={`ca-badge${person.allowedCount > 0 ? ' is-on' : ''}`}
                        title={`${person.allowedCount} of ${person.totalCount} allowed`}
                      >
                        {person.allowedCount}/{person.totalCount}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          <section className="ca-panel ca-panel--partners">
            {!selectedPrimary ? (
              <div className="ca-placeholder-card ca-placeholder-card--inset">
                <FontAwesomeIcon icon={faUserFriends} className="ca-placeholder-icon" />
                <h4>Select a {activeTab.primaryLabel.toLowerCase()}</h4>
                <p>Pick someone on the left to manage who they can message.</p>
              </div>
            ) : (
              <>
                <div className="ca-panel-head ca-panel-head--partners">
                  <div className="ca-selected">
                    <span className="ca-avatar ca-avatar--lg">
                      {getInitials(selectedPrimary.name)}
                    </span>
                    <div>
                      <h3>{selectedPrimary.name}</h3>
                      <p>
                        Can message {selectedPrimary.allowedCount} of {selectedPrimary.totalCount}{' '}
                        {activeTab.partnerLabel.toLowerCase()}
                      </p>
                    </div>
                  </div>

                  <div className="ca-bulk">
                    <button
                      type="button"
                      className="btn ca-btn ca-btn--allow"
                      disabled={bulkSaving || !partnerRows.length}
                      onClick={() => handleBulk(true)}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      Allow all
                    </button>
                    <button
                      type="button"
                      className="btn ca-btn ca-btn--block"
                      disabled={bulkSaving || !partnerRows.length}
                      onClick={() => handleBulk(false)}
                    >
                      <FontAwesomeIcon icon={faBan} />
                      Block all
                    </button>
                  </div>
                </div>

                <div className="ca-partner-toolbar">
                  <div className="search-box-wrap ca-search-box">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" aria-hidden="true" />
                    <input
                      type="search"
                      value={partnerSearch}
                      onChange={(e) => setPartnerSearch(e.target.value)}
                      placeholder={`Search ${activeTab.partnerLabel.toLowerCase()}`}
                      aria-label={`Search ${activeTab.partnerLabel}`}
                    />
                  </div>
                  <div className="ca-filter-pills">
                    {PARTNER_FILTERS.map((f) => (
                      <button
                        key={f.value}
                        type="button"
                        className={`ca-filter-pill${partnerFilter === f.value ? ' is-active' : ''}`}
                        onClick={() => setPartnerFilter(f.value)}
                      >
                        {f.value === 'allowed' && <FontAwesomeIcon icon={faUserCheck} />}
                        {f.value === 'blocked' && <FontAwesomeIcon icon={faUserLock} />}
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="ca-partner-list">
                  {filteredPartners.length === 0 ? (
                    <div className="ca-empty-inline">
                      No {activeTab.partnerLabel.toLowerCase()} match these filters.
                    </div>
                  ) : (
                    filteredPartners.map((row, index) => {
                      const key = `${row.user_low_id}-${row.user_high_id}`;
                      const busy = savingKey === key || bulkSaving;
                      return (
                        <div
                          key={key}
                          className={`ca-partner-row${index % 2 === 1 ? ' ca-partner-row--alt' : ''}`}
                        >
                          <span className="ca-avatar">{getInitials(row.partner?.name)}</span>
                          <div className="ca-person-meta">
                            <strong>{row.partner?.name}</strong>
                            <span>{row.partner?.username || row.partner?.role_name}</span>
                          </div>
                          <button
                            type="button"
                            className={`ca-switch${row.allowed ? ' is-on' : ''}`}
                            disabled={busy}
                            aria-pressed={!!row.allowed}
                            onClick={() => handleToggle(row)}
                          >
                            <span className="ca-switch-knob" />
                            <span className="ca-switch-label">
                              {row.allowed ? 'Allowed' : 'Blocked'}
                            </span>
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default ChatAccess;
